import { get as getAddon } from '@clevercloud/client/esm/api/v2/addon.js';
import { getZone } from '@clevercloud/client/esm/api/v4/product.js';
import { ONE_SECOND } from '@clevercloud/client/esm/with-cache.js';
import { sendToApi } from '../../lib/send-to-api.js';

/**
 * @import { RawAddon } from './cc-addon-header.types.js'
 * @import { Zone } from '../common.types.js'
 * @import { RawOperator } from '../../operators.types.js'
 * @import { ApiConfig } from '../../lib/send-to-api.types.js'
 */

export class CcAddonHeaderClient {
  /**
   * @param {object} params
   * @param {ApiConfig} params.apiConfig
   * @param {string} params.ownerId
   * @param {string} params.addonId
   * @param {string} params.providerId
   * @param {AbortSignal} params.signal
   */
  constructor({ apiConfig, ownerId, addonId, providerId, signal }) {
    this._apiConfig = apiConfig;
    this._ownerId = ownerId;
    this._addonId = addonId;
    this._provider = providerId;
    this._realId = null;
    this._signal = signal;
  }

  /** @return {Promise<RawAddon>} */
  getAddon() {
    return getAddon({ id: this._ownerId, addonId: this._addonId }).then(
      sendToApi({ apiConfig: this._apiConfig, signal: this._signal, cacheDelay: ONE_SECOND }),
    );
  }

  /**
   * @param {string} realId
   * @return {Promise<RawOperator>}
   */
  getOperator(realId) {
    return getOperator({ provider: this._provider, realId }).then(
      sendToApi({ apiConfig: this._apiConfig, signal: this._signal, cacheDelay: ONE_SECOND }),
    );
  }

  /**
   * @param {string} zoneName
   * @return {Promise<Zone>}
   */
  getZone(zoneName) {
    return getZone({ zoneName, ownerId: this._ownerId }).then(
      sendToApi({ apiConfig: this._apiConfig, signal: this._signal, cacheDelay: ONE_SECOND }),
    );
  }

  /** @returns {Promise<{ rawAddon: RawAddon, operator: RawOperator, zone: Zone }>} */
  async getAddonWithOperatorAndZone() {
    const rawAddon = await this.getAddon();
    this._realId = rawAddon.realId;
    const [operator, zone] = await Promise.all([this.getOperator(rawAddon.realId), this.getZone(rawAddon.region)]);

    return { rawAddon, operator, zone };
  }

  /** @returns {Promise<{ rawAddon: RawAddon, zone: Zone }>} */
  async getAddonWithZone() {
    const rawAddon = await this.getAddon();
    const zone = await this.getZone(rawAddon.region);

    return { rawAddon, zone };
  }

  /** @return {Promise<void>} */
  async restartAddon() {
    return rebootOperator({ provider: this._provider, realId: this._realId }).then(
      sendToApi({ apiConfig: this._apiConfig }),
    );
  }

  /** @return {Promise<void>} */
  async rebuildAndRestartAddon() {
    return rebuildOperator({ provider: this._provider, realId: this._realId }).then(
      sendToApi({ apiConfig: this._apiConfig }),
    );
  }
}

/**
 * @param {{ provider: string, realId: string }} params
 * @returns {Promise<Object>}
 */
function getOperator(params) {
  return Promise.resolve({
    method: 'get',
    url: `/v4/addon-providers/addon-${params.provider}/addons/${params.realId}`,
    headers: { Accept: 'application/json' },
    // no queryParams
    // no body
  });
}

// FIXME: remove and use the clever-client call from the new clever-client
/** @param {{ provider: string, realId: string }} params */
function rebootOperator(params) {
  // no multipath for /self or /organisations/{id}
  return Promise.resolve({
    method: 'post',
    url: `/v4/addon-providers/addon-${params.provider}/addons/${params.realId}/reboot`,
    headers: { Accept: 'application/json' },
    // no queryParams
    // no body
  });
}

// FIXME: remove and use the clever-client call from the new clever-client
/** @param {{ provider: string, realId: string }} params */
function rebuildOperator(params) {
  // no multipath for /self or /organisations/{id}
  return Promise.resolve({
    method: 'post',
    url: `/v4/addon-providers/addon-${params.provider}/addons/${params.realId}/rebuild`,
    headers: { Accept: 'application/json' },
    // no queryParams
    // no body
  });
}
